import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FuelConsumptionResponse } from './types';
import ChartErrorState from './ChartErrorState';
import './FuelConsumptionTable.scss';

interface FuelConsumptionTableProps {
  data: FuelConsumptionResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const formatNum = (value: number): string =>
  value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FuelConsumptionTable: FC<FuelConsumptionTableProps> = ({ data, isLoading, error, onRetry }) => {
  const { t } = useTranslation();

  if (error && !isLoading) {
    return (
      <div className="fuel-consumption-table">
        <h3 className="fuel-consumption-table__title">{t('cpi.analytics.fuelConsumption')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fuel-consumption-table fuel-consumption-table--loading">
        <h3 className="fuel-consumption-table__title">{t('cpi.analytics.fuelConsumption')}</h3>
        <div className="fuel-consumption-table__skeleton" />
      </div>
    );
  }

  if (!data || data.summary.length === 0) {
    return (
      <div className="fuel-consumption-table">
        <h3 className="fuel-consumption-table__title">{t('cpi.analytics.fuelConsumption')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="fuel-consumption-table">
      <h3 className="fuel-consumption-table__title">{t('cpi.analytics.fuelConsumption')}</h3>
      <div className="fuel-consumption-table__wrapper">
        <table className="fuel-consumption-table__table">
          <thead>
            <tr>
              <th>{t('cpi.analytics.fuelType')}</th>
              <th>{t('cpi.analytics.quantity')}</th>
              <th>WTT</th>
              <th>TTW</th>
              <th>WTW</th>
            </tr>
          </thead>
          <tbody>
            {data.summary.map((row) => (
              <tr key={row.fuelType}>
                <td>{row.fuelType}</td>
                <td className="fuel-consumption-table__num">{formatNum(row.quantity)}</td>
                <td className="fuel-consumption-table__num">{formatNum(row.wttEmissionsKg)}</td>
                <td className="fuel-consumption-table__num">{formatNum(row.ttwEmissionsKg)}</td>
                <td className="fuel-consumption-table__num">{formatNum(row.wtwEmissionsKg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FuelConsumptionTable;
